import { t } from '@/i18n';

export type MarketErrorCode = 'invalid_key' | 'rate_limited' | 'not_found' | 'network' | 'upstream' | 'no_source';

export class MarketError extends Error {
  constructor(
    public readonly code: MarketErrorCode,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'MarketError';
  }
}

/** fetch() with the provider's HTTP status mapped onto our error codes. */
export async function marketFetch(url: string, init: RequestInit = {}, label: string): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new MarketError('network', t('err.connect', { label }));
  }
  if (res.ok) return res;
  let detail = '';
  try {
    detail = (await res.text()).slice(0, 160);
  } catch {
    // ignore
  }
  const suffix = detail ? `: ${detail}` : '';
  if (res.status === 401 || res.status === 403) throw new MarketError('invalid_key', t('err.invalidKey', { label, status: res.status, detail: suffix }), res.status);
  if (res.status === 429) throw new MarketError('rate_limited', t('err.rateLimited', { label }), res.status);
  if (res.status === 404) throw new MarketError('not_found', t('err.notFound', { label }), res.status);
  throw new MarketError('upstream', t('err.upstream', { label, status: res.status, detail: suffix }), res.status);
}

export function describeMarketError(e: unknown): string {
  if (e instanceof MarketError) return e.message;
  if (e instanceof Error) return e.message || t('common.unknownError');
  return String(e);
}
